import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { capitalize } from 'lodash/string';
import moment from 'moment';
import anchorme from 'anchorme';
import './ticketCard.scss';
import InnerHtmlContent from '../InnerHtmlContent';

const TicketCard = ({ selectedTicketCode, ticket, unit, allResources, allGroups, allUnits, onClick, t }) => {
  const asMoment = date => moment(date, 'YYYY-MM-DD');
  const createdDate = asMoment(ticket.createdDate);
  const openingDate = asMoment(ticket.openingDate);
  const closingDate = asMoment(ticket.closingDate);
  const activationDate = asMoment(ticket.activationDate);
  const isActive = activationDate.isSameOrBefore(moment());
  const { clientNotes } = ticket;
  const translatedClientNotes = t(clientNotes);
  const filteredClientNotes = anchorme(translatedClientNotes, {
    attributes: [
      {
        name: 'target',
        value: '_blank'
      }
    ] });
  const active = selectedTicketCode === ticket.code && isActive;
  const { resources, group } = ticket;
  const timeSlotDefinitions = ticket.timeSlotDefinitions ? ticket.timeSlotDefinitions[0] : null;
  const groupResources = [];
  const resourcesAsObject = allGroups[group];
  const hasValidGroup = group && Object.hasOwnProperty.call(allGroups, group);
  const hasInvalidGroup = group && !Object.hasOwnProperty.call(allGroups, group);
  const disabled = !isActive || hasInvalidGroup;
  const disabledCardClass = disabled ? 'disabled-card time-slot-card' : '';
  if (hasValidGroup) {
    Object.keys(resourcesAsObject).forEach(res => groupResources.push(resourcesAsObject[res]));
  }
  return (
    <div className="ticket-card-container" >
      <Button
        className={`ticket-card ${disabledCardClass}`}
        active={active}
        onClick={() => { onClick(ticket) }}
        block
        bsStyle="default"
        bsSize="large"
        disabled={hasInvalidGroup}
      >
        <div className="ticket-card-left">
          <div className="ticket-card-main">
            <div className="ticket-card-date">
              <div className="ticket-card-label ticket-card-duration-label">{t('ticket.validPeriod')}: </div>
              <div className="ticket-card-data">{capitalize(openingDate.format('D.M.YYYY'))} – {capitalize(closingDate.format('D.M.YYYY'))}</div>
            </div>
            <div className="ticket-card-duration">
              <div className="ticket-card-label ticket-card-duration-label">{t('ticket.duration')}: </div>
              <div className="ticket-card-data">{`${ticket.duration ? ticket.duration : timeSlotDefinitions.duration} ${t('ticket.minutes')} `}</div>
            </div>
            <div className="ticket-created">
              <div className="ticket-card-label ticket-card-created-label">{t('ticket.created')}: </div>
              <div className="ticket-card-data">{capitalize(createdDate.format('D.M.YYYY'))}</div>
            </div>
          </div>
          <div className="ticket-card-variables">
            {ticket.speciality &&
            <div className="ticket-card-speciality">
              <div className="ticket-card-label ticket-card-speciality-label">{t('ticket.professionals')}: </div>
              <div className="ticket-card-data">{t(`ticket.specialities.${ticket.speciality}`)}</div>
            </div>
            }
            {timeSlotDefinitions && timeSlotDefinitions.specialities && timeSlotDefinitions.specialities.length > 0 &&
              <div className="ticket-card-speciality">
                <div className="ticket-card-label ticket-card-speciality-label">{t('ticket.professionals')}: </div>
                {timeSlotDefinitions.specialities.map(s => (
                  <div key={s} className="ticket-card-data">{t(`ticket.specialities.${s}`)}</div>
                ))
                }
              </div>
            }

            { unit &&
            <div className="ticket-card-unit">
              <div className="ticket-card-label ticket-card-unit-label">{t('ticket.unit')}: </div>
              <div className="ticket-card-data ticket-card-unit-name">{unit.name}</div>
              <div className="ticket-card-data ticket-card-unit-address">{unit.address}, {unit.city}</div>
            </div>
            }

            {timeSlotDefinitions && timeSlotDefinitions.inUnits && timeSlotDefinitions.inUnits.length > 0 &&
            <div>
              <div className="ticket-card-label ticket-card-unit-label">{t('ticket.unit')}: </div>
              {timeSlotDefinitions.inUnits.map(u => allUnits[u]).map(timeslotUnit => (
              timeslotUnit && <div className="ticket-card-unit" key={timeslotUnit.name}>
                <div className="ticket-card-data ticket-card-unit-name">{timeslotUnit.name}</div>
                <div className="ticket-card-data ticket-card-unit-address">{timeslotUnit.address}, {timeslotUnit.city}</div>
              </div>))}
            </div>
            }

            { resources && resources.length > 0 &&
            <div className="ticket-card-resource">
              <div className="ticket-card-label ticket-card-resource-name-label">{t('ticket.professionals')}: </div>
              { resources.map(r => allResources[r]).map(res => (
                <div key={res.resource} className="ticket-card-data ticket-card-resource-name">{res.name}</div>
              )) }
            </div>
            }
            { group && hasValidGroup &&
            <div className="ticket-card-resource">
              <div className="ticket-card-label ticket-card-resource-name-label">{t('ticket.professionals')}: </div>
              <div className="ticket-card-data ticket-card-resource-name">{groupResources.join(', ')}</div>
            </div>
            }
            { timeSlotDefinitions && timeSlotDefinitions.inGroups && timeSlotDefinitions.inGroups.length > 0 &&
            <div>
              <div className="ticket-card-label ticket-card-resource-name-label">{t('ticket.professionals')}: </div>
              {timeSlotDefinitions.inGroups.map(u => allGroups[u]).map((timeslotGroup, i) => (
                timeslotGroup && <div key={new Date().getTime()} className="ticket-card-data ticket-card-resource-name">{Object.values(timeslotGroup).join(', ')}</div>
              ))}
            </div>
            }
          </div>
          {filteredClientNotes && filteredClientNotes.length > 1 &&
          <div className="ticket-notes">
            <div className="ticket-card-label ticket-card-notes-label">{t('ticket.notes')}:</div>
            <div className="ticket-card-data" >
              <InnerHtmlContent content={filteredClientNotes} />
            </div>
          </div>
          }
        </div>
        <div className="ticket-card-right">
          {hasInvalidGroup &&
          null
          }
          {isActive && !hasInvalidGroup &&
          <div className="ticket-card-action-text">{t('time_slot_call_to_action')}</div>
          }
          {!isActive &&
          <div className="ticket-activation">
            <div className="ticket-card-label ticket-card-activation-label">{t('ticket.activates')}</div>
            <div className="ticket-card-data">{capitalize(activationDate.format('D.M.YYYY'))}</div>
          </div>
          }
        </div>
      </Button>
      { hasInvalidGroup &&
      <span className="ticket-card-label ticket-card-resource-name-label">{t('ticket.invalidTicket')}</span>
      }
    </div>
  );
};

TicketCard.propTypes = {
  t: PropTypes.func.isRequired,
  selectedTicketCode: PropTypes.string.isRequired,
  ticket: PropTypes.shape({
    openingDate: PropTypes.string.isRequired,
    closingDate: PropTypes.string.isRequired,
    activationDate: PropTypes.string.isRequired,
    code: PropTypes.string,
    cancelled: PropTypes.bool.isRequired,
    timeType: PropTypes.string,
    clientNotes: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    title: PropTypes.string
  }).isRequired,
  unit: PropTypes.shape({
    name: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    info: PropTypes.string
  }),
  allResources: PropTypes.object.isRequired,
  allGroups: PropTypes.object.isRequired,
  allUnits: PropTypes.object.isRequired,
  onClick: PropTypes.func
};

TicketCard.defaultProps = {
  unit: undefined,
  group: undefined,
  onClick: () => {}
};

export default TicketCard;



// WEBPACK FOOTER //
// ./src/components/TicketCard/index.jsx